import { Break, Nodes, Paragraph, Parent, PhrasingContent, RootContent, Text } from 'mdast';

/**
 * Renders HTML typed into markdown as the text that was typed, rather than as HTML.
 *
 * So `<b>text</b>` in a message comes out as the five characters `<b>`, then `text`, then
 * `</b>` — not as bold text, and not as nothing.
 *
 * Markdown lets raw HTML through, and nothing here wants it: a chat message and a form
 * instruction are written by people, read by everyone, and stored as plain text. The reason to
 * shut the whole door rather than pick tags off is that the harmful ones and the harmless ones
 * arrive by the same route — `<script>` and `<iframe>` are holes, `<img>` is a request to an
 * arbitrary URL the moment anybody opens the message, and `<b>` is a feature nobody asked for
 * that no editor of ours can produce. Supporting none of them is one rule; supporting some is a
 * list to keep correct forever.
 *
 * The parser is already not building HTML — the renderer omits `rehype-raw`, without which a
 * markdown `html` node reaches the tree as a `raw` node that React ignores. That silently *eats*
 * the tags and everything they wrap, so `<b>bold</b>` came out as nothing at all. Turning the
 * node into text ahead of that puts the characters back on screen exactly as they were typed,
 * which is what chat has always done with them.
 */
export function remarkLiteralHtml() {
  return (tree: Nodes) => literalizeHtml(tree, true);
}

function literalizeHtml(node: Nodes, isRoot: boolean) {
  if (!isParent(node)) {
    return;
  }

  const parent: Parent = node;
  parent.children = parent.children.map(child => {
    if (child.type === 'html') {
      // At the top level an HTML node stood for a block, so it becomes a paragraph rather than
      // loose text between two of them.
      return isRoot ? asParagraph(child.value) : asText(child.value);
    }
    literalizeHtml(child, false);
    return child;
  });
}

/** A node that can hold others. Narrowing on `children` is what tells the two apart. */
function isParent(node: Nodes): node is Nodes & Parent {
  return 'children' in node;
}

function asText(value: string): Text {
  return { type: 'text', value };
}

function asParagraph(value: string): Paragraph {
  return { type: 'paragraph', children: [asText(value)] };
}

/**
 * Makes a single newline a line break.
 *
 * CommonMark folds one into a space and only starts a new block at a blank line, which is the
 * right answer for a document and the wrong one for a message: people press return in chat to
 * start a new line, not to start a new paragraph. Chat's own renderer has always kept those
 * breaks, and a message that loses them loses its shape — a list of times, an address, a set of
 * steps all run together into one line.
 *
 * Done here rather than with `remark-breaks`, which is the same twenty lines: adding the package
 * churned this repo's lockfile into re-resolving `@types/react` and `esbuild`, which broke the
 * build in fifty places that have nothing to do with markdown.
 */
export function remarkLineBreaks() {
  return (tree: Nodes) => breakLines(tree);
}

function breakLines(node: Nodes) {
  if (!isParent(node)) {
    return;
  }

  const parent: Parent = node;
  const children: RootContent[] = [];

  for (const child of parent.children) {
    if (child.type === 'text' && child.value.includes('\n')) {
      children.push(...splitLines(child.value));
      continue;
    }
    breakLines(child);
    children.push(child);
  }

  parent.children = children;
}

/** `a\nb` becomes `a`, a break, `b` — and a trailing newline is a break with nothing after it. */
function splitLines(value: string): PhrasingContent[] {
  const out: PhrasingContent[] = [];

  value.split('\n').forEach((line, index) => {
    if (index > 0) {
      out.push(lineBreak());
    }
    if (line) {
      out.push(text(line));
    }
  });

  return out;
}

function text(value: string): Text {
  return { type: 'text', value };
}

function lineBreak(): Break {
  return { type: 'break' };
}
